import RHFTextField from "$components/rhf/text-field";
import { SelectField as RHFSelectField } from "$components/rhf-fields";
import { PALETTE } from "$global-providers/mui-provider/palette";
import type { DoseUnit } from "$global-providers/rxdb-provider/collections/prescription";
import { doseMessages } from "$routes/_lib/prescription-strings";
import { styled, Typography } from "@material-ui/core";
import { AdministrationApproach, RateUnit, TimeUnit } from "elephant-web/gql";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import type { AddPrescriptionCardFieldNames } from "./add-prescription-card";
import { RHFDoseUnitSelectField } from "./dose-unit-select-field";

const JAVA_MAX_SAFE_INTEGER_VALUE = 2147483646;
const MAX_INT = Yup.number()
  .positive()
  .max(JAVA_MAX_SAFE_INTEGER_VALUE, "Entered value is too high");

export const FREQUENT_DOSAGE_VALIDATION = Yup.object({
  doseValue: MAX_INT.min(1, "Must be over 0").required(),
  doseUnit: Yup.mixed<DoseUnit>().required(),
  rateValue: MAX_INT.min(1, "Must be over 0").required(),
  rateUnit: Yup.mixed<RateUnit>().required(),
  howLongForValue: MAX_INT.min(1, "Must be over 0").required(),
  howLongForUnit: Yup.mixed<TimeUnit>().required(),
});
export type FrequentDosageFormValues = Yup.InferType<
  typeof FREQUENT_DOSAGE_VALIDATION
>;

export const SINGLE_DOSAGE_VALIDATION = Yup.object({
  doseValue: MAX_INT.min(1, "Must be over 1").required(),
  doseUnit: Yup.mixed<DoseUnit>().required(),
});
export type SingleDosageFormValues = Yup.InferType<
  typeof SINGLE_DOSAGE_VALIDATION
>;

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const NarrowTextField = styled(RHFTextField)(() => ({
  width: "104px",
}));

const WideSelectField = styled(RHFSelectField)(() => ({
  "& .MuiOutlinedInput-input": {
    padding: "12px 24px 12px 0px",
    caretColor: PALETTE.primary.main,
  },
  "& .MuiOutlinedInput-root": {
    display: "flex",
    alignItems: "center",
  },
}));

const Span = styled("span")(({ theme }) => ({
  color: theme.palette.grey[700],
  alignItems: "center",
  fontSize: "1rem",
}));

export const HowLongForInputs: FC<{
  fieldNames: { value: string; unit: string };
}> = ({ fieldNames }) => {
  return (
    <Row>
      <NarrowTextField
        hideHelper={true}
        type="number"
        name={fieldNames.value}
        label="Number"
        required
        // TODO: replace this with a working solution! might have to make it a controlled component
        inputProps={{ min: 1 }}
      />
      <WideSelectField
        name={fieldNames.unit}
        hideHelper={true}
        required
        options={[
          { label: "Hour(s)", value: TimeUnit.Hour },
          { label: "Day(s)", value: TimeUnit.Day },
          { label: "Week(s)", value: TimeUnit.Week },
          { label: "Month(s)", value: TimeUnit.Month },
        ]}
      />
    </Row>
  );
};

export const DoseUnitField: FC<{
  administrationApproach: AdministrationApproach;
  fieldNames: Pick<
    AddPrescriptionCardFieldNames,
    "doseValue" | "doseUnit" | "clinicalProductId"
  >;
}> = ({ administrationApproach, fieldNames }) => {
  const { formatMessage } = useIntl();
  const { watch } = useFormContext();
  const [watchedClinicalProductId, watchedDoseUnit, watchedDoseValue] = watch([
    fieldNames.clinicalProductId,
    fieldNames.doseUnit,
    fieldNames.doseValue,
  ]);

  const translatedDoseUnit = useMemo(
    () =>
      doseMessages[watchedDoseUnit]
        ? formatMessage(doseMessages[watchedDoseUnit], {
            value: watchedDoseValue,
          })
        : watchedDoseUnit,
    [watchedDoseValue, formatMessage, watchedDoseUnit],
  );
  return (
    <>
      {watchedClinicalProductId ? (
        <Span>
          <FormattedMessage
            id="add-prescription.prefilled-dose-form"
            defaultMessage={`${translatedDoseUnit}${
              administrationApproach === AdministrationApproach.AsRequired
                ? ", up to "
                : ""
            }`}
          />
        </Span>
      ) : (
        <RHFDoseUnitSelectField
          name={fieldNames.doseUnit}
          required={!!watchedClinicalProductId}
        />
      )}
    </>
  );
};

export const FrequentDosageInputs: FC<{
  fieldNames: Pick<
    AddPrescriptionCardFieldNames,
    "doseValue" | "doseUnit" | "rateValue" | "rateUnit" | "clinicalProductId"
  >;
  administrationApproach: AdministrationApproach;
}> = ({ fieldNames, administrationApproach }) => {
  return (
    <>
      <Typography style={{ marginBottom: 8 }} variant="body1">
        <FormattedMessage
          id={
            administrationApproach === AdministrationApproach.AsRequired
              ? "prescription.maximumDosage"
              : "prescription.dosage"
          }
          defaultMessage={
            administrationApproach === AdministrationApproach.AsRequired
              ? "Maximum Dosage"
              : "Dosage"
          }
        />
      </Typography>
      <Row>
        <NarrowTextField
          name={fieldNames.doseValue}
          hideHelper={true}
          label="Dose"
          type="number"
          required
        />
        <DoseUnitField
          administrationApproach={administrationApproach}
          fieldNames={{
            clinicalProductId: fieldNames.clinicalProductId,
            doseUnit: fieldNames.doseUnit,
            doseValue: fieldNames.doseValue,
          }}
        />
        {(administrationApproach === AdministrationApproach.Outpatient ||
          administrationApproach === AdministrationApproach.AsRequired) && (
          <>
            <NarrowTextField
              hideHelper={true}
              name={fieldNames.rateValue}
              label="Number"
              type="number"
              required
              // TODO: replace this with a working solution! might have to make it a controlled component
              inputProps={{ min: 1 }}
            />
            <Span>
              <FormattedMessage
                id="add-prescription.times-per"
                defaultMessage="times per"
              />
            </Span>
            <WideSelectField
              name={fieldNames.rateUnit}
              hideHelper={true}
              required
              options={[
                { label: "Day", value: RateUnit.Day },
                { label: "Month", value: RateUnit.Month },
                { label: "Week", value: RateUnit.Week },
              ]}
            />
          </>
        )}
      </Row>
    </>
  );
};
